import {
  MutableRefObject,
  ReactNode,
  Suspense,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control } from "react-hook-form";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { generatePath, useNavigate } from "react-router";
import { logger } from "@/common/logger";
import { InferOutput, object, picklist, pipe, string, trim } from "valibot";
import { inc as semverIncrement } from "semver-ts";
import { Code02Icon, Loading01Icon, SlashDividerIcon } from "@/icons";
import { Button, ButtonProps } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredStar,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import { useValibotForm } from "@/utils/validation";
import { toast } from "@/utils/toast";
import { relayErrorMessage } from "@/utils/relay";
import { optionalSemverSchema } from "@/utils/schema";
import { formatSlug } from "@/utils/format";
import { useDebounced } from "@/utils/hooks";
import BrandModalContent from "@/components/BrandModalContent";
import EntityOwnerSelect, { OwnerOption } from "@/components/EntityOwnerSelect";
import WorkspaceAccessField from "@/components/WorkspaceAccessField";
import { EntityProfilePicFragment$key } from "./__generated__/EntityProfilePicFragment.graphql";
import { WorkspaceVersionCloneButtonVersionFragment$key } from "./__generated__/WorkspaceVersionCloneButtonVersionFragment.graphql";
import {
  WorkspaceVersionCloneButtonViewerFragment$data,
  WorkspaceVersionCloneButtonViewerFragment$key,
} from "./__generated__/WorkspaceVersionCloneButtonViewerFragment.graphql";
import { WorkspaceVersionCloneButtonMutation } from "./__generated__/WorkspaceVersionCloneButtonMutation.graphql";
import { WorkspaceVersionCloneButtonTrackQuery } from "./__generated__/WorkspaceVersionCloneButtonTrackQuery.graphql";
import { WorkspaceVersionCloneButtonExistsQuery } from "./__generated__/WorkspaceVersionCloneButtonExistsQuery.graphql";
import { splitWorkspaceSlug } from "@/utils/routing";

const ViewerFragment = graphql`
  fragment WorkspaceVersionCloneButtonViewerFragment on User {
    id
    username
    ...EntityProfilePicFragment @arguments(thumbnail: true)

    organizations {
      edges {
        node {
          organization {
            id
            username
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
        }
      }
    }
  }
`;

const VersionFragment = graphql`
  fragment WorkspaceVersionCloneButtonVersionFragment on WorkspaceVersion {
    id
    version
    workspace {
      id
      slug
      localSlug
      private
      owner {
        id
      }
    }
  }
`;

const Mutation = graphql`
  mutation WorkspaceVersionCloneButtonMutation(
    $id: ID!
    $input: CloneWorkspaceVersionInput!
    $asEntity: UsernameOrID
  ) {
    cloneWorkspaceVersion(id: $id, input: $input, asEntity: $asEntity) {
      id
      version
      workspace {
        id
        slug
      }
    }
  }
`;

const TrackQuery = graphql`
  query WorkspaceVersionCloneButtonTrackQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Competition {
        id
        slug
        title
        membership {
          id
          asEntity {
            id
            username
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
        }
        events(first: 1) {
          nodes {
            event {
              slug
            }
          }
        }
      }
    }
  }
`;

// Probes whether the target owner already has a workspace with the target slug,
// which decides whether the clone adds a version to it (semver field) or creates
// a fresh workspace (public/private field).
const ExistsQuery = graphql`
  query WorkspaceVersionCloneButtonExistsQuery(
    $owner: String!
    $slug: String!
  ) {
    workspaceBySlug(owner: $owner, slug: $slug) {
      id
    }
  }
`;

export interface WorkspaceVersionCloneButtonProps extends Omit<
  ButtonProps,
  "children"
> {
  version: WorkspaceVersionCloneButtonVersionFragment$key;
  viewer: WorkspaceVersionCloneButtonViewerFragment$key;
  children?: ReactNode;
  /// Clone as a starting point for this competition (track): the clone's
  /// owner is the viewer's team for the track, and non-members are prompted
  /// to join first.
  trackId?: string | null;
  /// Controlled mode: when `open` is set the built-in trigger button is not
  /// rendered and the caller owns the dialog's open state.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function WorkspaceVersionCloneButton({
  version: versionFragment,
  viewer: viewerFragment,
  children,
  trackId,
  open,
  onOpenChange: onOpenChangeProp,
  ...props
}: WorkspaceVersionCloneButtonProps) {
  const version = useFragment(VersionFragment, versionFragment);
  const viewer = useFragment(ViewerFragment, viewerFragment);
  const [internalOpen, setInternalOpen] = useState(false);
  const isMutationInFlight = useRef(false);
  const controlled = open !== undefined;

  const isOpen = controlled ? open : internalOpen;
  const onOpenChange = (open: boolean) => {
    if (isMutationInFlight.current) return;
    if (!controlled) setInternalOpen(open);
    onOpenChangeProp?.(open);
  };
  // Close on success, bypassing the in-flight guard above.
  const close = () => {
    if (!controlled) setInternalOpen(false);
    onOpenChangeProp?.(false);
  };

  const formProps = {
    versionId: version.id,
    sourceVersion: version.version,
    defaultSlug: version.workspace.localSlug ?? "",
    sourceOwnerId: version.workspace.owner.id,
    sourcePrivate: version.workspace.private,
    viewer,
    isMutationInFlight,
    onClose: close,
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={onOpenChange}>
      {!controlled && (
        <ResponsiveModalTrigger asChild>
          <Button {...props}>
            {children ?? <FormattedMessage defaultMessage="Clone" />}
          </Button>
        </ResponsiveModalTrigger>
      )}

      <BrandModalContent
        icon={<Code02Icon className="text-brand-500 w-8 h-8" />}
        title={
          <FormattedMessage
            defaultMessage="Clone {slug} v{version}"
            values={{
              slug: version.workspace.slug,
              version: version.version,
            }}
          />
        }
        description={
          <FormattedMessage defaultMessage="Copy this version into a workspace of your own to build on." />
        }
      >
        {trackId ? (
          <Suspense
            fallback={
              <div className="flex justify-center p-6">
                <Loading01Icon className="w-5 h-5 animate-spin" />
              </div>
            }
          >
            <TrackGate trackId={trackId} {...formProps} />
          </Suspense>
        ) : (
          <CloneForm {...formProps} />
        )}
      </BrandModalContent>
    </ResponsiveModal>
  );
}

const FormSchema = (intl: IntlShape) =>
  object({
    owner: string(),
    // Empty values fall back to the server defaults (the source's slug and
    // version, respectively).
    slug: pipe(string(), trim()),
    version: optionalSemverSchema(intl),
    // Only applied when the clone creates a new workspace; ignored otherwise.
    access: picklist(["PUBLIC", "PRIVATE"]),
  });
type SubmitData = InferOutput<ReturnType<typeof FormSchema>>;

type TrackTeam = {
  id: string;
  username: string;
} & EntityProfilePicFragment$key;

interface CloneFormProps {
  versionId: string;
  sourceVersion: string;
  defaultSlug: string;
  sourceOwnerId: string;
  sourcePrivate: boolean;
  viewer: WorkspaceVersionCloneButtonViewerFragment$data;
  isMutationInFlight: MutableRefObject<boolean>;
  onClose: () => void;
  /// When set, the clone is offered as a starting point for this track: the
  /// owner defaults to (and is locked to) the viewer's team for the track,
  /// until the user opts out with "Don't link to track".
  trackId?: string;
  trackTeam?: TrackTeam;
  trackTitle?: string;
}

function TrackGate({
  trackId,
  ...props
}: CloneFormProps & { trackId: string }) {
  const { node: track } =
    useLazyLoadQuery<WorkspaceVersionCloneButtonTrackQuery>(TrackQuery, {
      id: trackId,
    });

  // An unknown or unreadable track referral falls back to a normal clone.
  if (track?.__typename !== "Competition") {
    return <CloneForm {...props} />;
  }

  if (!track.membership) {
    const eventSlug = track.events.nodes[0]?.event.slug;
    return (
      <div className="self-stretch flex flex-col gap-4">
        <p className="text-sm text-center text-grey-600">
          <FormattedMessage
            defaultMessage="Join the track {title} to clone this template. Your clone will belong to your team for the track."
            values={{ title: <b>{track.title}</b> }}
          />
        </p>
        <div className="flex gap-2 lg:flex-wrap flex-wrap-reverse">
          <ResponsiveModalClose asChild>
            <Button
              variant="secondary"
              className="basis-full md:basis-auto md:flex-1"
            >
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
          </ResponsiveModalClose>
          {eventSlug && (
            <Link
              variant="primary"
              className="basis-full md:basis-auto md:flex-1"
              to={generatePath("/challenges/:owner/tracks/:slug", {
                owner: eventSlug,
                slug: track.slug,
              })}
            >
              <FormattedMessage defaultMessage="Go to the track" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <CloneForm
      {...props}
      trackId={track.id}
      trackTeam={track.membership.asEntity}
      trackTitle={track.title}
    />
  );
}

function CloneForm(props: CloneFormProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const formSchema = useMemo(() => FormSchema(intl), [intl]);
  const form = useValibotForm(formSchema, {
    defaultValues: {
      // In track mode the owner starts locked to the viewer's team for the
      // track (the server enforces this while `competitionId` is sent).
      owner: props.trackTeam?.id ?? props.viewer.id,
      slug: props.defaultSlug,
      version: "",
      access: props.sourcePrivate ? "PRIVATE" : "PUBLIC",
    },
  });
  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspaceVersionCloneButtonMutation>(Mutation);
  props.isMutationInFlight.current = isMutationInFlight;

  // Track clones default to linking to the track; the user can opt out to
  // clone into an owner of their own choosing instead.
  // `linkedToTrack` can only be true when `trackId` is set (it starts as
  // `!!trackId` and the relink control only renders in track mode).
  const [linkedToTrack, setLinkedToTrack] = useState(!!props.trackId);
  const forTrack = linkedToTrack;

  const owner = form.watch("owner");
  const slug = form.watch("slug");

  const unlinkFromTrack = () => {
    setLinkedToTrack(false);
    form.setValue("owner", props.viewer.id, { shouldDirty: true });
  };
  const relinkToTrack = () => {
    setLinkedToTrack(true);
    if (props.trackTeam) form.setValue("owner", props.trackTeam.id);
  };

  // Owner options: the viewer and their orgs, plus the locked track team (in
  // track mode) when it isn't already among them, so the disabled select can
  // still render it as the selected value.
  const ownerOptions: OwnerOption[] = useMemo(() => {
    const base: OwnerOption[] = [
      {
        id: props.viewer.id,
        username: props.viewer.username,
        entity: props.viewer,
      },
      ...(props.viewer.organizations?.edges ?? []).map(
        ({ node: { organization } }) => ({
          id: organization.id,
          username: organization.username,
          entity: organization,
        }),
      ),
    ];
    const team = props.trackTeam;
    if (
      team &&
      linkedToTrack &&
      !base.some((option) => option.id === team.id)
    ) {
      return [{ id: team.id, username: team.username, entity: team }, ...base];
    }
    return base;
  }, [props.viewer, props.trackTeam, linkedToTrack]);

  // Resolve the selected owner to a username so we can probe for an existing
  // workspace under it.
  const ownerUsername =
    ownerOptions.find((option) => option.id === owner)?.username ??
    props.viewer.username;
  const resolvedSlug = slug.trim() || props.defaultSlug;
  const effectiveSlug = formatSlug(resolvedSlug);
  const viewerIsOwner = owner === props.viewer.id;

  // Cloning into the source workspace itself: the default version equals the
  // source's, which the server rejects (must be strictly greater). Warn early.
  // Not applicable for a track clone, where the server derives the target owner.
  const isSameWorkspace =
    !forTrack &&
    owner === props.sourceOwnerId &&
    resolvedSlug === props.defaultSlug;

  // Cloning into an existing workspace can't reuse the source version (the new
  // version must be strictly greater), so default to the next patch there.
  const defaultVersion = isSameWorkspace
    ? (semverIncrement(props.sourceVersion, "patch") ?? props.sourceVersion)
    : props.sourceVersion;

  // Debounce the existence probe so typing a slug doesn't refetch per keystroke.
  const probe = useDebounced(
    useMemo(
      () => ({ owner: ownerUsername, slug: effectiveSlug }),
      [ownerUsername, effectiveSlug],
    ),
    350,
  );

  const onSubmit = (submitData: SubmitData) =>
    commitMutation({
      variables: {
        id: props.versionId,
        input: {
          slug: submitData.slug || null,
          version: submitData.version || defaultVersion,
          competitionId: forTrack ? props.trackId : null,
          // Applied only when the clone creates a new workspace; the server
          // preserves an existing target workspace's own visibility.
          private: submitData.access === "PRIVATE",
        },
        // The server derives the owner (the viewer's team for the track) when
        // linking to a track; otherwise it clones into the selected owner.
        asEntity: forTrack ? null : submitData.owner,
      },
      onCompleted: ({ cloneWorkspaceVersion: version }) => {
        props.onClose();
        navigate(
          generatePath("/:owner/:slug/versions/:version", {
            ...splitWorkspaceSlug(version.workspace.slug),
            version: version.version,
          }),
        );
        toast.success(
          <FormattedMessage
            defaultMessage="Cloned into {slug} v{version}"
            values={{
              slug: version.workspace.slug,
              version: version.version,
            }}
          />,
        );
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      className="self-stretch flex flex-col gap-4"
    >
      <div className="self-stretch flex md:flex-row flex-col md:items-start gap-2">
        <FormField
          control={form.control}
          name="owner"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>
                <FormattedMessage defaultMessage="Owner" />
                <RequiredStar />
              </FormLabel>
              <FormControl>
                <EntityOwnerSelect
                  options={ownerOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={forTrack}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <SlashDividerIcon className="md:block hidden h-10 w-8 text-grey-500 relative top-8" />

        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem className="flex-1 md:flex-3">
              <FormLabel>
                <FormattedMessage defaultMessage="Workspace slug" />
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder={props.defaultSlug} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {props.trackId && (
        <p className="text-sm text-grey-600">
          {linkedToTrack ? (
            <FormattedMessage
              defaultMessage="This clone will be linked to {title} and owned by your team for the track. <action>Don't link</action>"
              values={{
                title: <b>{props.trackTitle}</b>,
                action: (chunks) => (
                  <button
                    type="button"
                    onClick={unlinkFromTrack}
                    className="font-medium text-brand-500 hover:underline"
                  >
                    {chunks}
                  </button>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="This clone won't be linked to a track. <action>Link to {title}</action>"
              values={{
                title: props.trackTitle,
                action: (chunks) => (
                  <button
                    type="button"
                    onClick={relinkToTrack}
                    className="font-medium text-brand-500 hover:underline"
                  >
                    {chunks}
                  </button>
                ),
              }}
            />
          )}
        </p>
      )}

      <hr />

      <Suspense fallback={<FieldFallback />}>
        <ConditionalCloneFields
          control={form.control}
          defaultVersion={defaultVersion}
          ownerUsername={probe.owner}
          slug={probe.slug}
          viewerIsOwner={viewerIsOwner}
        />
      </Suspense>

      <div className="flex gap-2 lg:flex-wrap flex-wrap-reverse">
        <ResponsiveModalClose asChild>
          <Button
            type="button"
            variant="secondary"
            disabled={isMutationInFlight}
            className="basis-full md:basis-auto md:flex-1"
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
        </ResponsiveModalClose>

        <Button
          type="submit"
          disabled={isMutationInFlight}
          className="basis-full md:basis-auto md:flex-1"
        >
          {isMutationInFlight && (
            <Loading01Icon className="w-5 h-5 animate-spin" />
          )}
          <FormattedMessage defaultMessage="Clone" />
        </Button>
      </div>
    </Form>
  );
}

function FieldFallback() {
  return (
    <div className="flex items-center justify-center min-h-[76px]">
      <Loading01Icon className="w-5 h-5 animate-spin text-grey-400" />
    </div>
  );
}

// Shows the semver field when the target workspace already exists (the clone
// adds a version) or the public/private field when it will be created.
function ConditionalCloneFields({
  control,
  defaultVersion,
  ownerUsername,
  slug,
  viewerIsOwner,
}: {
  control: Control<SubmitData>;
  defaultVersion: string;
  ownerUsername: string;
  slug: string;
  viewerIsOwner: boolean;
}) {
  const data = useLazyLoadQuery<WorkspaceVersionCloneButtonExistsQuery>(
    ExistsQuery,
    { owner: ownerUsername, slug },
    { fetchPolicy: "store-or-network" },
  );

  return data.workspaceBySlug ? (
    <VersionField control={control} defaultVersion={defaultVersion} />
  ) : (
    <WorkspaceAccessField viewerIsOwner={viewerIsOwner} />
  );
}

function VersionField({
  control,
  defaultVersion,
}: {
  control: Control<SubmitData>;
  defaultVersion: string;
}) {
  const intl = useIntl();
  return (
    <FormField
      control={control}
      name="version"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            <FormattedMessage defaultMessage="Version" />
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              placeholder={intl.formatMessage(
                { defaultMessage: "Defaults to v{version}" },
                { version: defaultVersion },
              )}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
